export default function _enhancedSelectInput() {
    // Component accessibility reference:
    // https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html

    /**
     * Note: I had to define the SVG here as I couldn't
     * find a way to import it or reference it when the
     * markup is added via JavaScript
     * @const {string}
     */
    const SVG_CHEVRON =
        '<svg class="svg-icon" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.0001 16L12.0001 26L10.6001 24.6L19.2001 16L10.6001 7.4L12.0001 6L22.0001 16Z" fill="currentColor"/></svg>';

    /**
     * Component item selector
     * @const {string}
     */
    const SELECT_INPUT_SELECTOR = '.js-enhanced-select';

    /**
     * An additional width to add to the popup to
     * allow for the bolded text hover/focus styling
     * @const {string}
     */
    const POPUP_BOLDED_OFFSET = 10;

    /**
     * The number of milliseconds that the popup CSS transition
     * runs for when the popup is closed.
     * This is used to remove the inline styles added by
     * JavaScript when the popup is opened.
     * @const {integer}
     */
    const POPUP_TRANSITION_DELAY = 200;

    class EnhancedSelect {
        constructor(component) {
            // The source select HTML element
            this.sourceInput = component;
            // Semi-unique ID for this component, used for accessiblity IDs and labels
            this.componentId = `enhancedSelect${Math.ceil(
                Math.random() * 10000
            )}`;
            this.generateComponentMarkup =
                this.generateComponentMarkup.bind(this);
            this.openPopupButton = null;
            this.openPopupButtonLabel = null;
            this.selectOptionButtons = null;
            this.selectOptionPopup = null;
            this.onSelectOpenClick = this.onSelectOpenClick.bind(this);
            this.onSelectOptionClick = this.onSelectOptionClick.bind(this);
            this.getOptionLabelByValue = this.getOptionLabelByValue.bind(this);
            this.togglePopupMenu = this.togglePopupMenu.bind(this);
            this.onKeyPress = this.onKeyPress.bind(this);
            this.selectOptionsArray = [];
            this.currentSelectedOptionId = '';
            this.popupTransitionTimeout = null;

            const initialOptionValue =
                this.sourceInput.options[this.sourceInput.selectedIndex].value;
            const initialOptionLabel =
                this.sourceInput.options[this.sourceInput.selectedIndex].text;
            const selectOptionsCollection = this.sourceInput.options;

            // Capture the select options
            for (let i = 0, j = selectOptionsCollection.length; i < j; ++i) {
                this.selectOptionsArray.push({
                    label: selectOptionsCollection[i].label,
                    value: selectOptionsCollection[i].value,
                });
            }

            // Hide the source input
            this.sourceInput.style.display = 'none';

            // Insert the replacement markup
            this.sourceInput.insertAdjacentHTML(
                'afterend',
                this.generateComponentMarkup(
                    initialOptionLabel,
                    initialOptionValue,
                    this.selectOptionsArray
                )
            );

            // Capture a reference to the popup button
            this.openPopupButton =
                this.sourceInput.nextSibling.getElementsByClassName(
                    'js-enhanced-select-open'
                )[0];

            this.openPopupButtonLabel =
                this.openPopupButton.getElementsByClassName(
                    'js-enhanced-select-label'
                )[0];

            // Add the listener for the popup button
            this.openPopupButton.addEventListener(
                'click',
                this.onSelectOpenClick
            );

            // Capture a reference to the select button(s)
            this.selectOptionButtons =
                this.sourceInput.nextSibling.getElementsByClassName(
                    'js-enhanced-select-option'
                );

            // Add the listener for the select button(s)
            for (let k = 0, b = this.selectOptionButtons.length; k < b; ++k) {
                this.selectOptionButtons[k].addEventListener(
                    'click',
                    this.onSelectOptionClick
                );
            }

            // Capture a reference to the popup menu
            this.selectOptionPopup =
                this.sourceInput.nextSibling.getElementsByClassName(
                    'js-enhanced-select-popup'
                )[0];

            // Listen for key presses
            this.selectOptionPopup.addEventListener(
                'keydown',
                this.onKeyPress,
                false
            );
        }

        /**
         * Create the markup that replaces the default <select> input
         *
         * @param {String} buttonLabel The initial label for the button that opens the popup
         * @param {String} selectedOption The initial option for the select replacement
         * @param {Array} selectOptions Objects with "label" and "value" keys for the select options
         *
         * @returns String
         */
        generateComponentMarkup(buttonLabel, selectedOption, selectOptions) {
            return `<div class="enhanced-select__container">
                        <span class="sr-only">Click the following button to make a new selection. Your current selection is: </span>
                        <button
                            aria-controls="${this.componentId}listbox"
                            aria-expanded="false"
                            aria-activedescendant=""
                            aria-haspopup="listbox"
                            role="combobox"
                            class="js-enhanced-select-open enhanced-select__button"
                            type="button"
                            tabindex="0"
                        >
                            <span class="js-enhanced-select-label enhanced-select__button-label">${buttonLabel}</span>
                            ${SVG_CHEVRON}
                        </button>
                        <div
                            role="listbox"
                            id="${this.componentId}listbox"
                            tabindex="-1"
                            class="js-enhanced-select-popup enhanced-select-popup__container">
                            <span class="sr-only">Press the Escape key to close this popup.</span>
                            <div class="enhanced-select-popup__list-wrapper">
                                <div class="enhanced-select-popup__list-scroller">
                                    <ul class="enhanced-select-popup__list">
                                        ${selectOptions
                                            .map((option, index) => {
                                                const optionId = `${this.componentId}-${index}`;
                                                const optionIsSelected =
                                                    option.value ===
                                                    selectedOption;
                                                let additionalClasses = '';

                                                if (optionIsSelected) {
                                                    this.currentSelectedOptionId =
                                                        optionId;
                                                    additionalClasses =
                                                        'selected';
                                                }

                                                return `<li class="enhanced-select-popup__list-item">
                                                            <button
                                                                id="${optionId}"
                                                                role="option"
                                                                class="js-enhanced-select-option enhanced-select-popup__option ${additionalClasses}"
                                                                data-option-value="${option.value}"
                                                                aria-selected="${optionIsSelected}"
                                                            >
                                                                ${option.label}
                                                            </button>
                                                        </li>`;
                                            })
                                            .join('\n')}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>`;
        }

        /**
         * Respond to clicks on the button that opens the popup menu.
         *
         * @param {Click Event} evt Event that triggered the click
         *
         * @returns void
         */
        onSelectOpenClick(evt) {
            evt.preventDefault();

            // Set some accessibility attributes
            this.openPopupButton.setAttribute('aria-expanded', 'true');
            this.openPopupButton.setAttribute(
                'aria-activedescendant',
                this.currentSelectedOptionId
            );

            // Open the popup
            this.togglePopupMenu(true);
        }

        /**
         * Respond to clicks on the option button(s).
         * Update the source input to use the new value and close the popup.
         * Update the popup button to reflect the new value.
         *
         * @param {Click Event} evt Event that triggered the click
         *
         * @returns void
         */
        onSelectOptionClick(evt) {
            const selectedValue =
                evt.currentTarget.getAttribute('data-option-value');

            evt.preventDefault();

            // Capture the ID of the selected option
            this.currentSelectedOptionId = evt.currentTarget.getAttribute('id');

            // Remove the selected state and styles from all options
            for (let i = 0, j = this.selectOptionButtons.length; i < j; ++i) {
                this.selectOptionButtons[i].setAttribute(
                    'aria-selected',
                    'false'
                );

                this.selectOptionButtons[i].classList.remove('selected');
            }

            // Set the current item as selected
            evt.currentTarget.setAttribute('aria-selected', 'true');

            // Add a selected styling class
            evt.currentTarget.classList.add('selected');

            // Set the value in the source input
            this.sourceInput.value = selectedValue;

            // Replace the label of the popup button
            this.openPopupButtonLabel.innerText =
                this.getOptionLabelByValue(selectedValue);

            // Close the popup
            this.togglePopupMenu(false);
        }

        /**
         * Given a value for an <option>, return the corresponding label for that <option>
         *
         * @param {String} optionValue The option value
         *
         * @returns String
         */
        getOptionLabelByValue(optionValue) {
            let optionLabel = '';

            for (let i = 0, j = this.selectOptionsArray.length; i < j; ++i) {
                if (this.selectOptionsArray[i].value === optionValue) {
                    optionLabel = this.selectOptionsArray[i].label;
                    break;
                }
            }

            return optionLabel;
        }

        /**
         * Toggle the open state of the popup menu.
         *
         * Some inline style adjustments need to be made
         * to position the popup correctly.
         *
         * @param {Boolean} setOpen True will open the popup menu
         *
         * @returns void
         */
        togglePopupMenu(setOpen) {
            let popupButtonWidth = 0;
            let popupButtonHeight = 0;
            let popupContentWidth = 0;
            let popupContentHeight = 0;
            let popupMaxWidth = 0;

            // Clear the timeout that removes the popup inline styles
            if (typeof this.popupTransitionTimeout === 'number') {
                clearTimeout(this.popupTransitionTimeout);
            }

            if (setOpen) {
                /**
                 * Calculate the popup width, height, set the position
                 * and transition it into view
                 */

                // Get the current width of the popup button
                popupButtonWidth = Math.ceil(
                    this.openPopupButton.getBoundingClientRect().width
                );

                // Get the current height of the popup button
                popupButtonHeight = Math.ceil(
                    this.openPopupButton.getBoundingClientRect().height
                );

                /**
                 * Set the popup position as initially "fixed" so that
                 * JavaScript can calculate the content width correctly
                 */
                this.selectOptionPopup.style.position = 'fixed';

                popupContentWidth =
                    Math.ceil(
                        this.selectOptionPopup.getBoundingClientRect().width
                    ) + POPUP_BOLDED_OFFSET;

                popupContentHeight = Math.ceil(
                    this.selectOptionPopup
                        .getElementsByClassName(
                            'enhanced-select-popup__list-wrapper'
                        )[0]
                        .getBoundingClientRect().height
                );

                // Check the maximum width allowed for the popup
                popupMaxWidth = parseInt(
                    window
                        .getComputedStyle(this.selectOptionPopup, null)
                        .maxWidth.split('px')[0],
                    10
                );

                // Remove the inline style change
                this.selectOptionPopup.setAttribute('style', '');

                /**
                 * If the button is wider than the popup, then increase the
                 * popup width to match it
                 */
                if (popupButtonWidth > popupContentWidth) {
                    popupContentWidth = popupButtonWidth;
                }

                /**
                 * Catch any cases where the popup width might be wider
                 * than the allowed max-width
                 */
                if (popupContentWidth > popupMaxWidth) {
                    popupContentWidth = popupMaxWidth;
                }

                /**
                 * Set the popup width.
                 * Otherwise it's position property will override it's actual width.
                 */
                this.selectOptionPopup.style.width = `${popupContentWidth}px`;

                // Center the popup horizontally
                this.selectOptionPopup.style.left = `50%`;
                this.selectOptionPopup.style.marginLeft = `-${Math.ceil(
                    popupContentWidth / 2
                )}px`;

                // Center the popup vertically over the button
                this.selectOptionPopup.style.top = `-${Math.ceil(
                    popupContentHeight / 2 - popupButtonHeight / 2
                )}px`;

                // Open the popup
                this.selectOptionPopup.classList.add('is-active');

                // Set the keyboard focus on the currently selected option/button
                document.getElementById(this.currentSelectedOptionId).focus();

                // Scroll current option into view (as per WCAG example)
                // NOTE: This seems to already work with the .focus()
                // if (activeID === '' && !isElementInView(this.comboEl)) {
                //     this.comboEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                // }
            } else {
                // Close the popup
                this.selectOptionPopup.classList.remove('is-active');

                // Remove the popup inline styles after a short transition delay
                this.popupTransitionTimeout = setTimeout(() => {
                    this.selectOptionPopup.setAttribute('style', '');
                }, POPUP_TRANSITION_DELAY);

                // Reset some accessibility attributes
                this.openPopupButton.setAttribute('aria-expanded', 'false');
                this.openPopupButton.setAttribute('aria-activedescendant', '');
            }
        }

        /**
         * Respond to keyboard presses within the context of the popup container
         *
         * @param {KeyboardEvent} evt Event that triggered the click
         *
         * @returns void
         */
        onKeyPress(evt) {
            function switchElementFocus(direction) {
                // Get the current element in focus
                const currentFocusElement = document.activeElement;
                let switchOptionParent = null;

                // If it's an option from our popup then switch focus to the next option in the list
                if (
                    currentFocusElement.className.indexOf(
                        'js-enhanced-select-option'
                    ) !== -1
                ) {
                    if (direction === 'up') {
                        // Find the previous element in the list
                        switchOptionParent =
                            currentFocusElement.parentNode
                                .previousElementSibling;
                    } else {
                        // Find the next element in the list
                        switchOptionParent =
                            currentFocusElement.parentNode.nextElementSibling;
                    }

                    // We don't loop back around, we stop at the last option in the list
                    if (switchOptionParent !== null) {
                        switchOptionParent
                            .getElementsByClassName(
                                'js-enhanced-select-option'
                            )[0]
                            .focus();
                    }
                }
            }

            if (evt.key === 'ArrowUp') {
                switchElementFocus('up');
            }

            if (evt.key === 'ArrowDown') {
                switchElementFocus('down');
            }

            if (evt.key === 'Escape') {
                // Close the popup menu
                this.togglePopupMenu(false);
            }
        }
    }

    const components = document.querySelectorAll(SELECT_INPUT_SELECTOR);
    components.forEach((component) => {
        const DOMItem = component;

        // Attach the instance to the DOM once
        if (!DOMItem.component) {
            DOMItem.component = new EnhancedSelect(component);
        }
    });
}

_enhancedSelectInput();
